import { createScopedLogger, deepEqual, uuid } from "@@common";
import { IVariableScope } from "../../Models/IVariableScope";
import { VariableScopeObserver } from "./VariableScopeObserver";

const logger = createScopedLogger("ObjectVariableScope");

export class ObjectVariableScope implements IVariableScope {
  private static readonly caseInsensitiveKeyCache: WeakMap<Record<string, any>, Map<string, string>> = new WeakMap();

  private readonly baseId: string = uuid();
  private versionId: number = 0;
  private obj: Record<string, any>;
  private parent: IVariableScope | null = null;
  private parentKey: string | null = null;
  private parentPath: string | null = null;

  private observers?: Set<VariableScopeObserver>;

  // Child scopes that should receive fan-down notifications when a variable in this scope changes.
  // WeakRefs are used so that unmounted child scopes can be garbage-collected without explicit cleanup.
  private childScopes?: Set<WeakRef<ObjectVariableScope>>;

  // Single static notification queue to ensure all notifications are processed in order without races, even if multiple changes happen in quick succession across different scopes
  private static notificationQueue: Promise<void> = Promise.resolve();

  constructor(obj: Record<string, any> = {}, parent: IVariableScope | null = null, parentKey: string | null = null) {
    this.obj = obj;
    this.parent = parent;
    this.parentKey = parentKey;

    const parentPath = (parent as ObjectVariableScope)?.parentPath || null;

    this.parentPath = parentPath !== null && parentKey !== null ? `${parentPath}.${parentKey}` : parentPath !== null ? parentPath : parentKey;

    // Register with parent so it can fan notifications down to this child scope.
    (parent as ObjectVariableScope)?.registerChild?.(this);
  }

  /**
   * Registers a child scope to receive fan-down notifications.
   * Called automatically by child scopes in their constructor.
   */
  registerChild(child: ObjectVariableScope): void {
    if (!this.childScopes) {
      this.childScopes = new Set();
    }
    this.childScopes.add(new WeakRef(child));
  }

  get id(): string {
    return `${this.baseId}-v${this.versionId}`;
  }

  get stableId(): string {
    return this.baseId;
  }

  getId(): string {
    return this.id;
  }

  getParent(): IVariableScope | null {
    return this.parent;
  }

  addObserver(observer: VariableScopeObserver): void {
    if (!this.observers) {
      this.observers = new Set();
    }
    this.observers.add(observer);
  }

  removeObserver(observer: VariableScopeObserver): void {
    this.observers?.delete(observer);
  }

  getVariables(): string[] {
    // Return the variables in the current scope (not including parent scope)
    return Object.keys(this.obj);
  }

  /**
   * Gets a property from an object with case-insensitive fallback.
   * First tries exact match, then tries case-insensitive match (PascalCase vs camelCase tolerance).
   * @param obj The object to get property from
   * @param propertyName The property name to look up
   * @returns The actual property key to use, or undefined if property doesn't exist
   */
  private getCaseInsensitiveProperty(obj: Record<string, any>, propertyName: string): string | undefined {
    // First try exact match for performance and backward compatibility
    if (obj.hasOwnProperty(propertyName)) {
      return propertyName;
    }

    // Try case-insensitive match
    const lowerPropertyName = propertyName.toLowerCase();
    const cachedKey = ObjectVariableScope.caseInsensitiveKeyCache.get(obj)?.get(lowerPropertyName);
    if (cachedKey && obj.hasOwnProperty(cachedKey)) {
      return cachedKey;
    }

    const keys = Object.keys(obj);
    for (const key of keys) {
      if (key.toLowerCase() === lowerPropertyName) {
        let objectCache = ObjectVariableScope.caseInsensitiveKeyCache.get(obj);
        if (!objectCache) {
          objectCache = new Map();
          ObjectVariableScope.caseInsensitiveKeyCache.set(obj, objectCache);
        }
        objectCache.set(lowerPropertyName, key);
        return key;
      }
    }

    return undefined;
  }

  notifyChange(name: string | null, value: any, prevValue: any): void {
    // Early return when there is truly nothing to notify.
    if (!this.observers?.size && !this.childScopes?.size) {
      return;
    }

    // Build the full path (cached in parentPath, just append name if needed)
    const path = this.parentPath && name ? `${this.parentPath}.${name}` : this.parentPath || name;

    // Queue the notification work to be executed asynchronously in order
    ObjectVariableScope.notificationQueue = ObjectVariableScope.notificationQueue
      .then(async () => {
        // 1. Notify own observers.
        await this.notifyLocalObservers(path, value, prevValue);

        // 2. Fan down to all child scopes (e.g. SharedComponent child scopes that inherit
        //    variables from this ancestor scope).
        this.fanDownToChildren(path, value, prevValue);
      })
      .catch((error) => {
        logger.error("Error in notification queue:", error);
      });
  }

  /**
   * Called by an ancestor scope to propagate a variable change downward.
   * Notifies local observers and fans down further to any child scopes.
   * Does NOT bubble back up — that would create a notification cycle.
   */
  private receiveFromAncestor(path: string | null, value: any, prevValue: any): void {
    if (!this.observers?.size && !this.childScopes?.size) return;

    ObjectVariableScope.notificationQueue = ObjectVariableScope.notificationQueue
      .then(async () => {
        await this.notifyLocalObservers(path, value, prevValue);
        this.fanDownToChildren(path, value, prevValue);
      })
      .catch((error) => {
        logger.error("Error in ancestor notification:", error);
      });
  }

  /**
   * Notifies all local observers of a change. Extracted to avoid duplication
   * across notifyChange / notifyFromChild / receiveFromAncestor.
   */
  private async notifyLocalObservers(path: string | null, value: any, prevValue: any): Promise<void> {
    if (!this.observers?.size) return;
    for (const observer of this.observers) {
      const result = observer(this, path, value, prevValue);
      if (result instanceof Promise) await result;
    }
  }

  /**
   * Fans a change notification down to all registered child scopes.
   * Dead WeakRefs (GC'd child scopes) are pruned lazily during iteration.
   */
  private fanDownToChildren(path: string | null, value: any, prevValue: any): void {
    if (!this.childScopes?.size) return;
    const dead: WeakRef<ObjectVariableScope>[] = [];
    for (const ref of this.childScopes) {
      const child = ref.deref();
      if (child) {
        child.receiveFromAncestor(path, value, prevValue);
      } else {
        dead.push(ref);
      }
    }
    for (const ref of dead) this.childScopes.delete(ref);
  }

  /**
   * Traverses through a path of property names, optionally creating intermediate objects
   * @param path Array of property names to traverse
   * @param isAssignment If true, creates intermediate objects when they don't exist
   * @param startFrom Starting object/scope (defaults to this.obj for assignment, this for reading)
   * @param length Number of elements to traverse (defaults to full path length)
   * @returns The final object reached after  traversal, or undefined if path doesn't exist
   */
  private traversePath(path: string[], isAssignment: boolean, startFrom?: any, length?: number): any | undefined {
    let current: any = startFrom ?? (isAssignment ? this.obj : this);
    let currentScope: ObjectVariableScope | null = startFrom === this.obj ? null : this;
    const pathLength = length ?? path.length;

    if (current == null) {
      return undefined;
    }

    for (let i = 0; i < pathLength; i++) {
      const varName = path[i];

      // If current is an IVariableScope, use tryGet
      if (typeof current.tryGet === "function") {
        let next = current.tryGet(varName);
        // tryGet returns undefined when property doesn't exist, null when property exists with null value
        if (next === undefined) {
          if (isAssignment) {
            // Create a new ObjectVariableScope for this path element to maintain scope hierarchy
            next = new ObjectVariableScope({}, current, varName);
            current.set(varName, next);
          } else {
            // When reading, return undefined if property doesn't exist
            return undefined;
          }
        } else if (next === null && i < pathLength - 1) {
          // Property exists but is null, and we need to traverse further - can't continue
          return undefined;
        }
        currentScope = next as ObjectVariableScope;
        current = next;
      }
      // Otherwise, try to access as object property
      else if (typeof current === "object") {
        // Check if property exists with case-insensitive fallback
        const actualKey = this.getCaseInsensitiveProperty(current, varName);
        let next = actualKey ? current[actualKey] : undefined;

        if (!actualKey || (next == null && !isAssignment)) {
          if (isAssignment && !actualKey) {
            // Create a new ObjectVariableScope wrapped around a plain object to maintain notification chain
            const parentScope = currentScope || this;
            next = new ObjectVariableScope({}, parentScope, varName);
            current[varName] = next;
          } else if (!actualKey) {
            // When reading, return undefined if property doesn't exist
            return undefined;
          } else if (next == null && i < pathLength - 1) {
            // Property exists but is null/undefined and we need to traverse further
            return undefined;
          }
          // If we're at the last element and property exists, fall through to return the null value
        }

        currentScope = typeof next?.tryGet === "function" ? (next as ObjectVariableScope) : null;
        current = next;
      } else {
        return undefined;
      }

      if (isAssignment && current == null) {
        throw new Error(`Cannot navigate through null/undefined at path '${path.slice(0, i + 1).join(".")}'`);
      }

      if (isAssignment && typeof current !== "object") {
        throw new Error(`Cannot navigate through non-object type at path '${path.slice(0, i + 1).join(".")}'`);
      }
    }

    return current;
  }

  /**
   * Sets a property directly on this scope's underlying object without calling notifyChange.
   * Used by ancestor scopes when setting a nested property so that only ONE notification
   * fires (at the ancestor level) rather than one per traversal step.
   */
  private setLocalOnly(name: string, value: any): void {
    const keyToUse = this.getCaseInsensitiveProperty(this.obj, name) || name;
    this.obj[keyToUse] = value;
  }

  set(name: string | string[], value: any): void {
    // Handle path array for nested property access
    if (Array.isArray(name)) {
      if (name.length === 0) {
        throw new Error("Cannot assign to empty variable path");
      }

      if (name.length === 1) {
        // Single element path, use simple set
        this.set(name[0], value);
        return;
      }

      // Traverse to the parent object/scope, creating intermediate objects as needed
      const lastIndex = name.length - 1;

      // If the root variable doesn't exist locally but exists in a parent scope, delegate up
      const localKey = this.getCaseInsensitiveProperty(this.obj, name[0]);
      if (!localKey && this.parent && this.parent.tryGet(name[0]) !== undefined) {
        this.parent.set(name, value);
        return;
      }

      const current = this.traversePath(name, true, this.obj, lastIndex);

      const finalVarName = name[lastIndex];

      // If the intermediate node is an ObjectVariableScope, write its property directly and
      // fire notifyChange at THIS scope with the full path.  This keeps all notifications
      // anchored at the scope whose set() was originally called, eliminating the need for
      // bubble-up: parent scopes never need to "hear back" from a nested scope — they are
      // the one that initiated the set, so they own the notification.
      if (current instanceof ObjectVariableScope) {
        const nestedScope = current as ObjectVariableScope;
        const prevValue = nestedScope.tryGet(finalVarName);

        // Skip notification if value has not logically changed
        if (prevValue === value) return;
        if (!(value instanceof ObjectVariableScope) && !(prevValue instanceof ObjectVariableScope) && deepEqual(prevValue, value)) return;

        // Write directly into the nested scope without triggering its own notifyChange.
        nestedScope.setLocalOnly(finalVarName, value);
        // Increment version to indicate scope data has changed
        this.versionId++;
        // Notify at this (root) scope with the full dotted path so our own observers and
        // all fan-down children (e.g. SharedComponent child scopes) are notified correctly.
        this.notifyChange(name.join("."), value, prevValue);
      }
      // If the intermediate node is some other IVariableScope implementation, delegate normally.
      else if (typeof current.set === "function") {
        current.set(finalVarName, value);
      }
      // Otherwise, assign as object property and notify change on the root scope
      else if (typeof current === "object") {
        const prevValue = current[finalVarName];

        // Skip notification if value has not logically changed
        if (prevValue === value) return;
        if (!(value instanceof ObjectVariableScope) && !(prevValue instanceof ObjectVariableScope) && deepEqual(prevValue, value)) return;

        current[finalVarName] = value;

        // Increment version to indicate scope data has changed
        this.versionId++;
        // Notify from root scope with the full dotted path
        this.notifyChange(name.join("."), value, prevValue);
      } else {
        throw new Error(`Cannot assign to non-object type`);
      }
      return;
    }

    // Handle simple string name
    // If the variable does not exist in the current scope but exists in the parent, set it in the parent
    const actualKey = this.getCaseInsensitiveProperty(this.obj, name);
    if (!actualKey && this.parent && this.parent.tryGet(name) !== undefined) {
      this.parent.set(name, value);
      return;
    }

    // otherwise, set in the current scope (use actual key if found for case-insensitive match)
    const keyToUse = actualKey || name;
    const prevValue = this.obj[keyToUse];
    this.obj[keyToUse] = value;

    // Skip notification if value has not logically changed
    if (prevValue === value) return;
    if (!(value instanceof ObjectVariableScope) && !(prevValue instanceof ObjectVariableScope) && deepEqual(prevValue, value)) return;

    // Increment version to indicate scope data has changed
    this.versionId++;

    this.notifyChange(name, value, prevValue);
  }

  tryGet(name: string | string[]): any | undefined {
    // Handle path array for nested property access
    if (Array.isArray(name)) {
      if (name.length === 0) {
        return undefined;
      }

      if (name.length === 1) {
        // Single element path, use simple tryGet
        return this.tryGet(name[0]);
      }

      // Traverse through the path
      return this.traversePath(name, false, undefined, name.length);
    }

    // Handle simple string name
    // First check in the current scope (with case-insensitive fallback)
    const key = this.getCaseInsensitiveProperty(this.obj, name);
    if (key) {
      return this.obj[key];
    }
    // Then check in the parent scope if available
    return this.parent?.tryGet(name);
  }
}
